import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_JS_LOADER_OPTIONS } from "../../config/googleMapsJsLoaderOptions";
import {
  FiShare2, FiHeart, FiChevronDown, FiChevronUp,
  FiChevronRight, FiMapPin, FiPhone,
  FiArrowLeft, FiArrowRight, FiCalendar, FiClock, FiStar,
  FiChevronLeft,
} from "react-icons/fi";
import { FaRegBuilding } from "react-icons/fa";
import adBanner from "../../assets/new_homes/banner.png";
import axiosInstanceAuth from "../../apiInstances/axiosInstanceAuth";

// ── helpers ──────────────────────────────────────────────────────────────────

function buildPrice(data) {
  if (data?.price_display_checked && data?.price_display) return data.price_display;
  if (data?.price) return `₦${Number(data.price).toLocaleString()}`;
  return "Price on request";
}

function toParaArray(val) {
  if (Array.isArray(val) && val.length) return val;
  if (typeof val === "string" && val.trim()) {
    const parts = val.split(/\n\n+/).filter(Boolean);
    return parts.length ? parts : [val];
  }
  return [];
}

function normalizeDetail(raw) {
  if (!raw) return null;
  const nd = raw.newDevelopment || {};
  const la = raw.lead_agent || {};

  const addressParts = [raw.street_address_number, raw.street_address_name].filter(Boolean);
  const address = nd.address || addressParts.join(" ") || raw.suburb || raw.location || "";

  const lat = raw.lat ?? nd.lat ?? raw.location?.lat ?? null;
  const lng = raw.lng ?? nd.lng ?? raw.location?.lng ?? null;
  const mapLocation = lat && lng ? { lat: Number(lat), lng: Number(lng) } : null;

  const gallery = Array.isArray(nd.gallery) ? nd.gallery : [];
  const mainImage = raw.frontPageImg || nd.mainImage || gallery[0] || null;

  const description = toParaArray(nd.description || raw.description);

  const team = Array.isArray(raw.teamMembers) ? raw.teamMembers
    : Array.isArray(nd.teamMembers) ? nd.teamMembers
      : Array.isArray(nd.team) ? nd.team
        : [];

  const locations = Array.isArray(nd.locations) && nd.locations.length
    ? nd.locations
    : address
      ? [{ address, label: "PROJECT ADDRESS" }]
      : [];

  return {
    name: nd.projectName || raw.name || raw.title || raw.street_address_name || "Property",
    developer: nd.developer || la.name || "Developer",
    address,
    mapLocation,
    price: buildPrice(raw),
    types: raw.property_type ? [raw.property_type] : nd.types || [],
    mainImage,
    gallery: gallery.slice(1, 3),
    description,
    team,
    locations,
    displaySuite: Array.isArray(nd.displaySuite) ? nd.displaySuite : [],
    articles: Array.isArray(nd.articles) ? nd.articles : [],
    schools: nd.schools || { primary: [], secondary: [], childcare: [] },
    faqs: Array.isArray(nd.faqs) ? nd.faqs : [],
    agencyLogo: la.agencyLogo || null,
    agencyName: la.name || "La Rosa",
    primaryColor: raw.primary_color || "#3A3A3A",
    phone: nd.phone || la.phone || null,
    totalUnits: nd.totalUnits || nd.total_units || null,
    house_size: raw.house_size || nd.house_size || "",
    land_size: raw.land_size || nd.land_size || "",
  };
}

// ── sub-components ────────────────────────────────────────────────────────────

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b-[2px] border-[#EAEAEA] last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center py-4 text-left text-md font-medium text-[#000929] hover:text-[#097FE6] transition-colors"
      >
        <span className="pr-4">{q}</span>
        {open ? <FiChevronUp size={16} className="flex-shrink-0" /> : <FiChevronDown size={16} className="flex-shrink-0" />}
      </button>
      {open && <p className="pb-4 text-sm text-gray-500 leading-relaxed">{a}</p>}
    </div>
  );
};

const TeamAccordionRow = ({ member, defaultOpen = false }) => {
  const hasContent = !!(member.desc || member.description || member.developments);
  const [open, setOpen] = useState(defaultOpen && hasContent);
  return (
    <div className="border-b-[2px] border-[#EAEAEA] last:border-0">
      <button
        type="button"
        onClick={() => hasContent && setOpen((prev) => !prev)}
        className={`w-full flex justify-between items-center gap-4 py-4 text-left ${!hasContent ? "cursor-default" : ""}`}
      >
        <span className="text-md text-[#000929]">
          <span className="font-bold">{member.role}:</span>{" "}
          <span>{member.name}</span>
        </span>
        {hasContent && (
          open
            ? <FiChevronUp size={16} className="flex-shrink-0 text-[#097FE6]" />
            : <FiChevronDown size={16} className="flex-shrink-0 text-[#097FE6]" />
        )}
      </button>
      {open && (member.desc || member.description || member.developments) && (
        <div className="pb-5">
          {(member.desc || member.description) && <p className="text-gray-600 leading-relaxed mb-5">{member.desc || member.description}</p>}
          {member.developments && (
            <>
              <p className="text-sm font-semibold tracking-wide text-[#000929] uppercase mb-4">
                Completed Developments
              </p>
              <div className="grid grid-cols-3 gap-4">
                {member.developments.map((dev, i) => (
                  <div key={i} className="rounded-xl border-[1.5px] border-[#EAEAEA] overflow-hidden">
                    <div className="h-[150px]">
                      <img src={dev.image} alt={dev.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4">
                      <p className="text-sm font-semibold text-[#000929]">{dev.name}</p>
                      <p className="text-xs text-gray-400 my-2">{dev.type}</p>
                      <p className="text-xs text-[#000929]">{dev.address}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const DiamondIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="10" y="1.5" width="11" height="11" rx="2" transform="rotate(45 10 1.5)" stroke="#3B82F6" strokeWidth="1.5" />
  </svg>
);

const GridIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
  </svg>
);

// ── main component ────────────────────────────────────────────────────────────

const NewHomesDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_JS_LOADER_OPTIONS);
  const [saved, setSaved] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [schoolTab, setSchoolTab] = useState("primary");
  const [ctaMessage, setCtaMessage] = useState("");
  const [ctaChecks, setCtaChecks] = useState({});

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (!id) return;
    const userId = localStorage.getItem("userId") || null;
    setLoading(true);
    setFetchError(null);
    axiosInstanceAuth
      .post("Agency/viewProperty", { id, userId })
      .then((res) => {
        if (res?.data?.status) {
          setData(normalizeDetail(res.data.data));
        } else {
          setFetchError(res?.data?.message || "Failed to load property.");
        }
      })
      .catch((err) => {
        setFetchError(err?.response?.data?.message || err.message || "Failed to load property.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const onMapLoad = useCallback(
    (map) => {
      if (data?.mapLocation) {
        map.setCenter(data.mapLocation);
        map.setZoom(15);
      }
    },
    [data]
  );

  const toggleCheck = (key) => setCtaChecks((prev) => ({ ...prev, [key]: !prev[key] }));

  const articlesRef = useRef(null);
  const scrollArticles = (dir) => {
    const el = articlesRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (320 + 16), behavior: "smooth" });
  };

  const CTA_OPTIONS = [
    "Scheduling an inspection",
    "Price information",
    "Rates and fees",
    "Finding similar properties",
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#097FE6]" />
      </div>
    );
  }

  if (fetchError || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white gap-4">
        <p className="text-red-500 text-base">{fetchError || "Property not found."}</p>
        <button onClick={() => navigate(-1)} className="text-[#097FE6] font-semibold hover:underline">
          ← Go back
        </button>
      </div>
    );
  }

  const d = data;

  return (
    <div className="bg-white min-h-screen">

      {/* ── BREADCRUMB ── */}
      <div className="container pb-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-md text-[#097FE6] font-bold py-5">
          <FiChevronLeft size={18} className="text-[#097FE6] font-bold -mr-1" />
          Back to results
        </button>
        <nav className="flex items-center gap-2 text-md text-gray-500 flex-wrap">
          <button onClick={() => navigate("/")} className="hover:text-[#097FE6] transition-colors">Buy</button>
          <span className="px-2">/</span>
          <button onClick={() => navigate("/new-homes")} className="hover:text-[#097FE6] transition-colors">Nigeria</button>
          <span className="px-2">/</span>
          <span className="flex items-center gap-1 text-[#000929] font-medium">
            <FiMapPin size={13} className="text-[#097FE6]" />
            {d.address}
          </span>
        </nav>
      </div>

      {/* ── AGENCY BAR ── */}
      <div className="w-full h-[60px] flex items-center justify-center" style={{ backgroundColor: d.primaryColor }}>
        {d.agencyLogo ? (
          <img src={d.agencyLogo} alt={d.agencyName} className="h-9 w-auto object-contain" />
        ) : (
          <span className="text-white text-lg font-bold">{d.agencyName}</span>
        )}
      </div>

      {/* ── TITLE ROW ── */}
      <div className="container py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-[#000929] leading-10">{d.name}</h1>
            <p className="text-md mt-1 text-[#000929]/50">
              {d.address}{" "}
              <button className="text-[#097FE6] underline font-bold ml-1">View on Map</button>
            </p>
          </div>
          <div className="flex gap-3 flex-shrink-0 mt-1">
            <button className="flex items-center gap-2 h-12 px-6 rounded-full border-[1.5px] border-[#EAEAEA] text-[#000929] text-sm font-medium hover:bg-gray-50 transition-colors">
              <FiShare2 size={15} /> Share
            </button>
            <button
              onClick={() => setSaved(!saved)}
              className={`flex items-center gap-2 h-12 px-6 rounded-full border-[1.5px] text-sm font-medium transition-colors ${saved
                ? "border-red-300 text-red-500 bg-red-50"
                : "border-[#EAEAEA] text-[#000929] hover:bg-gray-50"
                }`}
            >
              <FiHeart size={15} className={saved ? "fill-red-500" : ""} />
              Favourite
            </button>
          </div>
        </div>
      </div>

      {/* ── HERO GALLERY ── */}
      <div className="container py-4">
        <div className="flex gap-8 h-[563px]">
          {/* main image */}
          <div className="flex-1 relative overflow-hidden rounded-2xl bg-gray-100">
            {d.mainImage ? (
              <img src={d.mainImage} alt={d.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">No image</div>
            )}
            <div className="absolute inset-x-0 bottom-0 h-[45%] bg-gradient-to-t from-black/75 via-black/40 to-transparent" />
            <div className="absolute bottom-5 left-5 z-10 flex items-baseline gap-2">
              <span className="text-white text-lg font-normal">From</span>
              <span className="text-white text-[26px] font-bold">{d.price}</span>
            </div>
            <div className="absolute bottom-6 right-5 z-10 flex items-center gap-2">
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className={`block rounded-full h-[5px] transition-all ${i === 0 ? "w-6 bg-white" : "w-2 bg-white/45"}`} />
              ))}
            </div>
          </div>

          {/* thumbnails */}
          <div className="flex-shrink-0 w-[384px] flex flex-col gap-[33px]">
            <div className="h-[265px] overflow-hidden rounded-2xl bg-gray-100">
              {d.gallery[0] ? (
                <img src={d.gallery[0]} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-200" />
              )}
            </div>
            <div className="h-[265px] relative overflow-hidden rounded-2xl bg-gray-100">
              {d.gallery[1] ? (
                <img src={d.gallery[1]} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-200" />
              )}
              <button className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-white/90 hover:bg-white text-gray-900 text-xs font-semibold px-3 py-1.5 rounded-lg shadow transition-colors">
                <GridIcon /> View all photos
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── CONTENT + SIDEBAR ── */}
      <div className="container py-4">
        <div className="flex gap-8 items-start">
          {/* LEFT MAIN CONTENT */}
          <div className="flex-1 min-w-0">
            {/* info card */}
            <div className="mb-8 bg-white rounded-2xl border-[1.5px] border-[#EAEAEA] p-8 flex flex-col gap-8">
              <div className="flex items-center gap-8">
                <FiMapPin size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-[#000929]/50 mb-1">Display Location</p>
                  <p className="text-base font-semibold text-[#000929]">{d.address || "—"}</p>
                </div>
              </div>
              <div className="h-px bg-[#EAEAEA] -my-4" />
              <div className="flex items-start gap-40">
                <div className="flex items-center gap-8">
                  <DiamondIcon />
                  <div>
                    <p className="text-xs text-[#000929]/50 mb-1">Total Size</p>
                    <p className="text-base font-semibold text-[#000929]">
                      {
                        d?.house_size == "" ? "-" :
                          <span className="text-base font-semibold text-[#000929]">
                            {d?.house_size} × {d?.land_size} m<sup>2</sup>
                          </span>
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <FaRegBuilding size={20} className="text-[#097FE6]" />
                  <div>
                    <p className="text-xs text-[#000929]/50 mb-1">Property type</p>
                    <p className="text-base font-semibold text-[#000929]">
                      {d.types.length ? d.types.join(", ") : "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* description */}
            {d.description.length > 0 && (
              <section className="pb-8 border-[1.5px] border-[#EAEAEA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#000929] mb-3">{d.name} Residences</h2>
                {(descExpanded ? d.description : d.description.slice(0, 2)).map((para, i) => (
                  <p key={i} className="text-gray-600 leading-relaxed mb-3">{para}</p>
                ))}
                {d.description.length > 2 && (
                  <button
                    type="button"
                    onClick={() => setDescExpanded((prev) => !prev)}
                    className="flex items-center gap-1 text-sm font-semibold text-[#097FE6] hover:text-blue-700"
                  >
                    {descExpanded ? "Show less" : "Show more"}
                    {descExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                  </button>
                )}
              </section>
            )}
            {/* map */}
            <section className="mt-8 pb-8 border-[1.5px] border-[#EAEAEA] rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-[#000929] mb-4">{d.name} locations</h2>
              <div className="rounded-xl overflow-hidden h-[421px] border-[1.5px] border-[#EAEAEA]">
                {isLoaded && d.mapLocation ? (
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={d.mapLocation}
                    zoom={14}
                    onLoad={onMapLoad}
                    options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                  >
                    <Marker position={d.mapLocation} />
                  </GoogleMap>
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-sm">
                    {isLoaded ? "Location unavailable" : <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#097FE6]" />}
                  </div>
                )}
              </div>
              {d.locations.length > 0 && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {d.locations.map((loc, i) => (
                    <div key={i} className="rounded-xl p-4">
                      <p className="text-xs text-gray-400 font-semibold mb-1 tracking-wider">{loc.label}</p>
                      <p className="text-base font-medium text-[#000929]">{loc.address}</p>
                      {loc.linkL && (
                        <a href={loc.linkL} target="_blank" rel="noopener noreferrer" className="text-sm text-[#097FE6] underline font-semibold flex items-center gap-1 mt-1">
                          Get directions
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
            {/* team */}
            {d.team.length > 0 && (
              <section className="mt-8 pb-8 border-[1.5px] border-[#EAEAEA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#000929] mb-4 pb-4 border-b-[2px] border-gray-100">
                  Team behind {d.name}
                </h2>
                {d.team.map((member, i) => (
                  <TeamAccordionRow key={member.role || i} member={member} defaultOpen={i === 0} />
                ))}
              </section>
            )}
            {/* display suite */}
            {d.displaySuite.length > 0 && (
              <section className="mt-8 pb-8 border-[1.5px] border-[#EAEAEA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#000929]">Visit the display suite</h2>
                <p className="text-[#000929]/50 mt-1 pb-4 border-b-[2px] border-[#EAEAEA]">Closed on public holidays</p>
                {d.displaySuite.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-5 border-b-[2px] border-[#EAEAEA]">
                    <div>
                      <p className="text-sm font-semibold text-[#000929]">{s.label}</p>
                      <p className="my-1 font-semibold text-[#000929]/50">{s.date}</p>
                      <p className="text-[#000929]/50 font-semibold">{s.time}</p>
                    </div>
                    <button className="text-sm text-white font-semibold bg-[#097FE6] rounded-full px-6 py-3 hover:bg-blue-700 transition-colors whitespace-nowrap">
                      Add to plan &amp; register
                    </button>
                  </div>
                ))}
                <div className="flex gap-4 mt-6">
                  <button className="flex-1 text-sm text-[#097FE6] font-semibold bg-blue-50 rounded-full py-3.5 hover:bg-blue-100 transition-colors">
                    See all times
                  </button>
                  <button className="flex-1 text-sm text-[#097FE6] font-semibold border-[1.5px] border-blue-200 rounded-full py-3.5 hover:bg-blue-50 transition-colors">
                    See my inspection plan
                  </button>
                </div>
              </section>
            )}
            {/* articles */}
            {d.articles.length > 0 && (
              <section className="mt-8 pb-8 border-[1.5px] border-[#EAEAEA] rounded-2xl p-8">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-2xl font-bold text-[#000929]">Recommended articles</h2>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => scrollArticles(-1)} className="w-10 h-10 flex items-center justify-center rounded-full border-[1.5px] border-[#EAEAEA] text-[#097FE6] hover:bg-blue-50 transition-colors">
                      <FiArrowLeft size={18} />
                    </button>
                    <button type="button" onClick={() => scrollArticles(1)} className="w-10 h-10 flex items-center justify-center rounded-full border-[1.5px] border-[#EAEAEA] text-[#097FE6] hover:bg-blue-50 transition-colors">
                      <FiArrowRight size={18} />
                    </button>
                  </div>
                </div>
                <div ref={articlesRef} className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                  {d.articles.map((a, idx) => (
                    <div key={a.id || idx} className="cursor-pointer group flex-shrink-0 w-[300px] snap-start rounded-2xl border-[1.5px] border-[#EAEAEA] overflow-hidden">
                      <div className="h-[200px] overflow-hidden bg-gray-100">
                        {a.image && <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />}
                      </div>
                      <div className="p-5">
                        <div className="flex items-center justify-between gap-4 text-xs text-gray-400 mb-3">
                          <span className="flex items-center gap-1.5"><FiCalendar size={13} /> {a.date}</span>
                          <span className="flex items-center gap-1.5"><FiClock size={13} /> {a.readTime} read</span>
                        </div>
                        <p className="text-base text-[#000929] line-clamp-2 group-hover:text-[#097FE6] transition-colors">{a.title}</p>
                        <button className="text-xs text-[#097FE6] mt-3 font-semibold flex items-center gap-1.5 hover:gap-2.5 transition-all">
                          Read Article <FiArrowRight size={15} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {/* schools */}
            {(d.schools.primary?.length > 0 || d.schools.secondary?.length > 0 || d.schools.childcare?.length > 0) && (
              <section className="mt-8 pb-8 border-[1.5px] border-[#EAEAEA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#000929] mb-8">Nearby schools &amp; child care</h2>
                <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-full mb-4">
                  {[{ key: "primary", label: "Primary" }, { key: "secondary", label: "Secondary" }, { key: "childcare", label: "Child care" }].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setSchoolTab(tab.key)}
                      className={`px-5 py-2 text-sm rounded-full transition-colors ${schoolTab === tab.key ? "bg-white text-[#000929] font-semibold shadow-sm" : "text-gray-400 font-medium hover:text-gray-600"}`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div>
                  {(d.schools[schoolTab] || []).map((s, i) => (
                    <div key={i} className="grid grid-cols-4 items-center py-5 border-b-[2px] border-[#EAEAEA] text-sm last:border-0">
                      <span className="text-[#000929]">{s.name}</span>
                      <span className="text-[#000929]">{s.type}</span>
                      <span className="text-[#000929]">{s.govt}</span>
                      <span className="text-[#000929] text-right">{s.distance}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {/* FAQs */}
            {d.faqs.length > 0 && (
              <section className="mt-8 mb-12 border-[1.5px] border-[#EAEAEA] rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-[#000929] mb-2">FAQs for {d.name} Apartments</h2>
                <div className="w-full h-[2px] bg-[#EAEAEA] my-4" />
                {d.faqs.map((faq, i) => (
                  <FAQItem key={i} q={faq.q || faq.question} a={faq.a || faq.answer} />
                ))}
              </section>
            )}
            {/* contact consultant */}
            <section className="mt-8 mb-12 border-[1.5px] border-[#EAEAEA] rounded-2xl overflow-hidden">
              <div className="flex items-center justify-center py-6" style={{ backgroundColor: d.primaryColor }}>
                {d.agencyLogo ? (
                  <img src={d.agencyLogo} alt={d.agencyName} className="h-9 w-auto object-contain" />
                ) : (
                  <span className="text-white font-light text-[26px] tracking-[0.05em]">{d.agencyName.toLowerCase()}</span>
                )}
              </div>

              <div className="p-8">
                <h2 className="text-2xl font-bold text-[#000929] mb-4">
                  Get in touch with a consultant to discuss {d.name} today
                </h2>

                {d.phone && (
                  <button className="inline-flex items-center h-8 px-2 mb-4 rounded-lg border-[1px] border-[#CFCFCF] text-sm text-[#000929] opacity-70 hover:bg-gray-50 transition-colors font-medium">
                    {d.phone}
                  </button>
                )}

                <textarea
                  rows={8}
                  placeholder="What would you like to ask the agent?"
                  value={ctaMessage}
                  onChange={(e) => setCtaMessage(e.target.value)}
                  className="w-full text-sm text-[#000929] placeholder:text-gray-400 rounded-xl border-[1.5px] border-[#EAEAEA] p-4 outline-none focus:border-[#097FE6] transition-colors resize-none"
                />

                <div className="flex items-center gap-2 mt-4 px-4 py-3 rounded-xl bg-[#EFF6FF] text-sm text-[#000929] border border-[#BFDBFE]">
                  <span className="flex items-center justify-center w-4 h-4 rounded-full border border-[#097FE6] text-[#097FE6] text-[10px] font-bold flex-shrink-0">i</span>
                  Add more detail to help the agents understand exactly what you need.
                </div>

                <p className="text-sm font-bold text-[#000929] mt-6 mb-3">What would you like help with?</p>
                <div className="grid grid-cols-2 gap-y-3 gap-x-6">
                  {CTA_OPTIONS.map((opt) => (
                    <label key={opt} className="flex items-center gap-2.5 text-sm text-[#000929] cursor-pointer">
                      <input type="checkbox" checked={!!ctaChecks[opt]} onChange={() => toggleCheck(opt)} className="w-4 h-4 rounded accent-[#097FE6]" />
                      {opt}
                    </label>
                  ))}
                </div>

                <p className="flex items-center gap-2 text-xs text-gray-400 mt-4">
                  <span className="flex items-center justify-center w-3.5 h-3.5 rounded-full border border-gray-400 text-[9px] font-bold flex-shrink-0">i</span>
                  This data is shared with a third party
                </p>

                <button className="w-full h-[52px] mt-5 bg-[#097FE6] hover:bg-blue-700 text-white font-semibold text-sm rounded-full transition-colors">
                  Next
                </button>
              </div>
            </section>
          </div>
          {/* RIGHT SIDEBAR */}
          <div className="w-[384px] flex-shrink-0 hidden lg:block sticky top-4">
            <div className="sticky top-4 space-y-4">

              {/* agency card */}
              <div className="rounded-2xl overflow-hidden border-[1.5px] border-[#EAEAEA]">
                <div className="flex items-center justify-center py-7" style={{ backgroundColor: d.primaryColor }}>
                  {d.agencyLogo ? (
                    <img src={d.agencyLogo} alt={d.agencyName} className="h-10 w-auto object-contain" />
                  ) : (
                    <span className="text-white font-light text-[32px] tracking-[0.02em]">{d.agencyName.toLowerCase()}</span>
                  )}
                </div>
                <div className="px-6 py-7 bg-white">
                  <p className="text-center font-bold text-xl text-[#000929] mb-5 leading-snug">
                    Get in touch with a consultant today.
                  </p>
                  {d.phone && (
                    <div className="flex justify-center mb-5">
                      <button className="inline-flex items-center h-9 px-4 rounded-lg border-[1.5px] border-[#EAEAEA] text-sm text-[#000929]/70 hover:bg-gray-50 transition-colors">
                        {d.phone}
                      </button>
                    </div>
                  )}
                  <button className="w-full flex items-center justify-center gap-2 h-[52px] bg-[#097FE6] hover:bg-blue-700 text-white font-semibold text-sm rounded-full transition-colors mb-3">
                    <FiPhone size={15} /> Get in touch
                  </button>
                  <button
                    onClick={() => setSaved(!saved)}
                    className={`w-full flex items-center justify-center gap-2 h-[52px] font-semibold text-sm rounded-full border-[1.5px] transition-colors ${saved
                      ? "border-[#097FE6] text-[#097FE6] bg-blue-50"
                      : "border-[#EAEAEA] text-[#000929] hover:bg-gray-50"
                      }`}
                  >
                    <FiStar size={15} className={saved ? "fill-[#097FE6]" : ""} />
                    {saved ? "Saved" : "Save property"}
                  </button>
                </div>
              </div>

              {/* ad banner */}
              <div className="rounded-2xl overflow-hidden border-[1.5px] border-[#EAEAEA]">
                <img src={adBanner} alt="Advertisement" className="w-full h-auto object-cover" />
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewHomesDetail;
